import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { operations as sitesOperations } from 'store/sitesManager'
import Unauthorized from 'secondstep-components/src/components/Unauthorized'
import UserManagement from './component'
import { UpdateSiteUserNotifications } from 'lib/util'
import { FILTER_USER_MAP } from './constants'
import {
  getFilterOptionFromQuery,
  memoActiveSiteUsers,
  memoFilteredUsersBySelectedOption,
  memoSelectedUserManagementSite,
  memoSiteLicenseFlags,
  memoUserFilterOptionsAndCounts,
} from './data'
import {
  handleFilterSiteUsers,
  handleSendInvites as sendInvitesHandler,
  handleSubmitAddUsers as submitAddUsersHandler,
} from './handlers'
import {
  selectAdminSites,
  selectSiteUsersForUserManagement,
  selectUserManagementBaseState,
} from './selectors'

export const UserManagementContainer = ({ pushTo }) => {
  const dispatch = useDispatch()
  const { siteId: siteIdParam } = useParams()
  const siteId = siteIdParam ? parseInt(siteIdParam, 10) : null

  const {
    addBulkUsersResponse,
    hasUnassignedLicenses,
    isAddBulkUsersCompleted,
    isLoading,
    isUpdating,
    location,
  } = useSelector(selectUserManagementBaseState)

  const adminSites = useSelector(selectAdminSites)

  // Local state
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [sendInvitesModalOpen, setSendInvitesModalOpen] = useState(false)
  const [selectedFilterOption, setSelectedFilterOption] = useState({
    key: 'all',
    name: FILTER_USER_MAP.all,
  })

  // Memos
  const site = useMemo(
    () => memoSelectedUserManagementSite(adminSites, siteId),
    [adminSites, siteId],
  )

  const siteUsers = useSelector(state =>
    selectSiteUsersForUserManagement(state, siteId, site),
  )

  const { hasK8License, hasSelaLicense } = useMemo(
    () => memoSiteLicenseFlags(site),
    [site],
  )

  const activeUsers = useMemo(() => memoActiveSiteUsers(siteUsers), [siteUsers])

  const {
    userCounts,
    userFilterOptions,
    userInviteDialogCounts,
  } = useMemo(
    () => memoUserFilterOptionsAndCounts(activeUsers, hasSelaLicense),
    [activeUsers, hasSelaLicense],
  )

  const users = useMemo(
    () =>
      memoFilteredUsersBySelectedOption(
        siteUsers,
        activeUsers,
        selectedFilterOption?.key,
      ),
    [siteUsers, activeUsers, selectedFilterOption?.key],
  )

  const lastFetchedSiteIdRef = useRef(null)

  // useCallbacks
  const fetchSiteUsers = useCallback(
    siteId => dispatch(sitesOperations.fetchSiteUsers(siteId)),
    [dispatch],
  )

  const fetchK8SiteUsers = useCallback(
    siteId => dispatch(sitesOperations.fetchK8SiteUsers(siteId)),
    [dispatch],
  )

  const createSiteUser = useCallback(
    (user, sendNotification, siteData) =>
      dispatch(
        sitesOperations.createSiteUser(user, sendNotification, siteData),
      ),
    [dispatch],
  )

  const createSiteUsers = useCallback(
    (siteId, users, sendNotification) =>
      dispatch(
        sitesOperations.createSiteUsers(siteId, users, sendNotification),
      ),
    [dispatch],
  )

  const remindSiteUser = useCallback(
    siteUser => dispatch(sitesOperations.remindSiteUser(siteUser)),
    [dispatch],
  )

  const remindSiteUsers = useCallback(
    (siteId, params) =>
      dispatch(sitesOperations.remindSiteUsers(siteId, params)),
    [dispatch],
  )

  const resetBulkAddUsers = useCallback(
    () => dispatch(sitesOperations.resetBulkAddUsers()),
    [dispatch],
  )

  const updateSiteUser = useCallback(
    (user, notification) =>
      dispatch(sitesOperations.updateSiteUser(user, notification)),
    [dispatch],
  )

  const filterSiteUsers = useCallback(
    event =>
      handleFilterSiteUsers(event, {
        site,
        pushTo,
        setSelectedFilterOption,
      }),
    [site, pushTo],
  )

  const onSiteChange = useCallback(
    e => {
      const newSiteId = e?.value?.id
      if (newSiteId && newSiteId !== siteId) {
        pushTo(`/user-management/${newSiteId}`)
      }
    },
    [siteId, pushTo],
  )

  const redirectToUserManagement = useCallback(() => {
    if (siteId) {
      pushTo(`/user-management/${siteId}`)
    }
  }, [siteId, pushTo])

  const redirectToLicenseManagement = useCallback(
    () => pushTo('/license-management'),
    [pushTo],
  )

  const handleSendInvites = useCallback(
    inviteSelection =>
      sendInvitesHandler(inviteSelection, {
        site,
        remindSiteUsers,
        setSendInvitesModalOpen,
      }),
    [site, remindSiteUsers],
  )

  const handleSubmitAddUsers = useCallback(
    (emails, role, sendNotification, inputMode) =>
      submitAddUsersHandler(emails, role, sendNotification, inputMode, {
        siteId,
        site,
        createSiteUsers,
        createSiteUser,
        resetBulkAddUsers,
        setIsAddUserModalOpen,
      }),
    [siteId, site, createSiteUser, createSiteUsers, resetBulkAddUsers],
  )

  const handleCloseAddUsersModal = useCallback(() => {
    resetBulkAddUsers()
    redirectToUserManagement()
    setIsAddUserModalOpen(false)
  }, [resetBulkAddUsers, redirectToUserManagement])

  const handleGrantNewRole = useCallback(
    (siteUser, newRole) => {
      updateSiteUser(
        { ...siteUser, roles: [newRole] },
        UpdateSiteUserNotifications.grantNewRole,
      )
    },
    [updateSiteUser],
  )

  const handleCancelUserAccess = useCallback(
    siteUser => {
      updateSiteUser(
        { ...siteUser, isActive: false },
        UpdateSiteUserNotifications.removeUser,
      )
    },
    [updateSiteUser],
  )

  const handleRemindEmail = remindSiteUser

  const handleOpenSendInvitesModal = () => setSendInvitesModalOpen(true)
  const handleCloseSendInvitesModal = () => setSendInvitesModalOpen(false)
  const handleOpenAddUsersModal = () => setIsAddUserModalOpen(true)

  // useEffects
  useEffect(() => {
    if (!siteId && site?.id) {
      pushTo(`/user-management/${site.id}`)
    }
  }, [siteId, site?.id, pushTo])

  useEffect(() => {
    if (!site?.id) return

    const siteIdChanged = lastFetchedSiteIdRef.current !== site.id
    const siteUsersMissing = siteUsers === null || siteUsers === undefined

    if (siteIdChanged || siteUsersMissing) {
      lastFetchedSiteIdRef.current = site.id
      fetchSiteUsers(site.id)
      if (hasK8License) {
        fetchK8SiteUsers(site.id)
      }
    }
  }, [site?.id, siteUsers, hasK8License, fetchSiteUsers, fetchK8SiteUsers])

  useEffect(() => {
    if (!siteUsers || activeUsers.length === 0) return

    const filterOption = getFilterOptionFromQuery(location?.query)
    setSelectedFilterOption(filterOption)
  }, [siteUsers, activeUsers.length, location?.query])

  // Unauthorized state - keep in container as it's an authorization/data concern
  // Only show Unauthorized when sites have loaded (not loading) and exist, but the specific site is not found
  // Check: if we have a siteId in the URL but the site doesn't exist in our sites array
  const hasSiteIdButSiteNotFound = siteId !== null && !site
  if (!isLoading && adminSites.length > 0 && hasSiteIdButSiteNotFound) {
    return <Unauthorized location={location} />
  }

  // Pass all props to component - let component handle loading and empty states
  return (
    <UserManagement
      addBulkUsersResponse={addBulkUsersResponse}
      allActiveUsersCount={userCounts}
      filterSiteUsers={filterSiteUsers}
      handleCancelUserAccess={handleCancelUserAccess}
      handleCloseAddUsersModal={handleCloseAddUsersModal}
      handleCloseSendInvitesModal={handleCloseSendInvitesModal}
      handleGrantNewRole={handleGrantNewRole}
      handleOpenAddUsersModal={handleOpenAddUsersModal}
      handleOpenSendInvitesModal={handleOpenSendInvitesModal}
      handleRemindEmail={handleRemindEmail}
      handleSendInvites={handleSendInvites}
      handleSubmitAddUsers={handleSubmitAddUsers}
      hasK8License={hasK8License}
      hasSelaLicense={hasSelaLicense}
      hasUnassignedLicenses={hasUnassignedLicenses}
      isAddBulkUsersCompleted={isAddBulkUsersCompleted}
      isAddUserModalOpen={isAddUserModalOpen}
      isLoading={isLoading}
      isSendInvitesModalOpen={sendInvitesModalOpen}
      isUpdating={isUpdating}
      onSiteChange={onSiteChange}
      redirectToLicenseManagement={redirectToLicenseManagement}
      selectedFilterOption={selectedFilterOption}
      selectedSite={site}
      selectedSiteUsers={users}
      siteUsers={siteUsers}
      sites={adminSites}
      updateSiteUser={updateSiteUser}
      userFilterOptions={userFilterOptions}
      userInviteDialogCounts={userInviteDialogCounts}
    />
  )
}

UserManagementContainer.propTypes = {
  pushTo: PropTypes.func.isRequired,
}

export default UserManagementContainer
